// src/components/AutomacaoMessages.js — Aba "Mensagens personalizadas"
//
// Biblioteca de mensagens freeform da company. Usadas por automações que
// rodam dentro da janela 24h (welcome first contact). Freeform = grátis,
// NÃO queima crédito de template.
//
// Layout: lista à esquerda + editor à direita (+ preview WhatsApp-style).
//
// Endpoints:
//   GET    /custom-messages
//   POST   /custom-messages         { name, body }
//   PATCH  /custom-messages/{id}    { name?, body? }
//   POST   /custom-messages/{id}/media  (multipart: file + kind='IMAGE'|'DOCUMENT')
//   DELETE /custom-messages/{id}/media
//   DELETE /custom-messages/{id}

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Stack, Button, IconButton, TextField, CircularProgress,
  Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  FiPlus, FiTrash2, FiImage, FiFileText, FiPaperclip, FiSave,
  FiMessageSquare, FiX, FiCheck,
} from 'react-icons/fi';
import api from '../services/Api';
import { C } from './automacao/tema';

const SYNE = "'Syne', system-ui, sans-serif";
const OUTFIT = "'Outfit', system-ui, sans-serif";

const MAX_BODY = 4000;

export default function AutomacaoMessages({ headers, onLibraryChanged, onToast }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState({ name: '', body: '' });
  const [dirty, setDirty] = useState(false);   // draft difere do sel salvo
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // {id, name}
  const fileRef = useRef(null);

  // ─── Fetch ─────────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/custom-messages', { headers });
      setList(res.data || []);
      // Se selectedId sumiu da lista, deseleciona.
      if (selectedId && !(res.data || []).some(m => m.id === selectedId)) {
        setSelectedId(null);
        setDraft({ name: '', body: '' });
        setDirty(false);
      }
    } catch (err) {
      onToast?.({ kind: 'error', msg: err.response?.data?.detail || 'Erro ao carregar mensagens.' });
    } finally {
      setLoading(false);
    }
  }, [headers, onToast, selectedId]);

  useEffect(() => { refresh(); }, [refresh]);

  const selected = list.find(m => m.id === selectedId) || null;

  // Ao trocar de seleção, popula o draft.
  useEffect(() => {
    if (selected) {
      setDraft({ name: selected.name, body: selected.body });
      setDirty(false);
    }
  }, [selected]);

  // ─── Actions ───────────────────────────────────────────────────────────
  const startNew = async () => {
    if (dirty && !window.confirm('Você tem alterações não salvas. Descartar?')) return;
    setSaving(true);
    try {
      const res = await api.post('/custom-messages', {
        name: 'Nova mensagem',
        body: 'Escreva sua mensagem aqui…',
      }, { headers });
      await refresh();
      setSelectedId(res.data.id);
      onLibraryChanged?.();
    } catch (err) {
      onToast?.({ kind: 'error', msg: err.response?.data?.detail || 'Erro ao criar.' });
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => {
    if (!selected) return;
    const name = (draft.name || '').trim();
    const body = (draft.body || '').trim();
    if (!name) return onToast?.({ kind: 'error', msg: 'Nome não pode ficar vazio.' });
    if (!body) return onToast?.({ kind: 'error', msg: 'Mensagem não pode ficar vazia.' });
    setSaving(true);
    try {
      await api.patch(`/custom-messages/${selected.id}`, { name, body }, { headers });
      await refresh();
      onLibraryChanged?.();
      setDirty(false);
      onToast?.({ kind: 'ok', msg: 'Mensagem salva.' });
    } catch (err) {
      onToast?.({ kind: 'error', msg: err.response?.data?.detail || 'Erro ao salvar.' });
    } finally {
      setSaving(false);
    }
  };

  const uploadMedia = async (file) => {
    if (!selected || !file) return;
    const mime = (file.type || '').toLowerCase();
    let kind = null;
    if (mime.startsWith('image/')) kind = 'IMAGE';
    else if (mime === 'application/pdf' || mime.includes('word') || mime === 'text/plain') kind = 'DOCUMENT';
    if (!kind) return onToast?.({ kind: 'error', msg: 'Formato não suportado. Use imagem (JPG/PNG/WebP) ou documento (PDF/DOC/TXT).' });

    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    setUploading(true);
    try {
      await api.post(`/custom-messages/${selected.id}/media`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      await refresh();
      onLibraryChanged?.();
      onToast?.({ kind: 'ok', msg: 'Anexo adicionado.' });
    } catch (err) {
      onToast?.({ kind: 'error', msg: err.response?.data?.detail || 'Erro ao subir anexo.' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeMedia = async () => {
    if (!selected || !selected.has_media) return;
    if (!window.confirm('Remover o anexo desta mensagem?')) return;
    setUploading(true);
    try {
      await api.delete(`/custom-messages/${selected.id}/media`, { headers });
      await refresh();
      onLibraryChanged?.();
    } catch (err) {
      onToast?.({ kind: 'error', msg: err.response?.data?.detail || 'Erro ao remover anexo.' });
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    try {
      await api.delete(`/custom-messages/${confirmDelete.id}`, { headers });
      setConfirmDelete(null);
      setSelectedId(null);
      await refresh();
      onLibraryChanged?.();
    } catch (err) {
      onToast?.({ kind: 'error', msg: err.response?.data?.detail || 'Erro ao apagar.' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header explicativo */}
      <Box sx={{
        p: 2, mb: 2, borderRadius: '10px',
        bgcolor: C.greenBg, border: `1px solid ${C.greenB}`,
      }}>
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <FiMessageSquare size={16} color={C.greenD} style={{ marginTop: 3, flexShrink: 0 }} />
          <Box>
            <Typography sx={{ fontFamily: SYNE, fontSize: '0.82rem', fontWeight: 700, color: C.greenD, mb: 0.5 }}>
              Sem custo de template Meta
            </Typography>
            <Typography sx={{ fontFamily: SYNE, fontSize: '0.76rem', color: C.text2, lineHeight: 1.55 }}>
              Estas mensagens são enviadas <b>dentro da janela de 24h</b> após o cliente escrever pra você —
              texto e mídia entram como <i>session message</i> e não consomem crédito de template. Use pra
              boas-vindas, respostas automáticas e follow-ups rápidos.
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
        gap: 2, alignItems: 'start',
      }}>
        {/* ── Lista ────────────────────────────────────────────── */}
        <Box sx={{
          bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px',
          overflow: 'hidden', boxShadow: C.sombra,
        }}>
          <Box sx={{ p: '12px 14px', borderBottom: `1px solid ${C.bdr}`, bgcolor: C.surf }}>
            <Button
              onClick={startNew}
              disabled={saving}
              startIcon={<FiPlus size={14} />}
              fullWidth
              variant="contained"
              sx={{
                bgcolor: C.green, color: C.sobreCor, fontFamily: SYNE, fontWeight: 700,
                textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
                fontSize: '0.78rem',
                '&:hover': { bgcolor: C.greenHover, boxShadow: 'none' },
              }}
            >
              Nova mensagem
            </Button>
          </Box>
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {loading ? (
              <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={20} sx={{ color: C.green }} /></Box>
            ) : list.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.76rem', color: C.text3 }}>
                  Nenhuma mensagem criada.
                </Typography>
                <Typography sx={{ fontFamily: SYNE, fontSize: '0.7rem', color: C.text3, mt: 0.5 }}>
                  Clique em "Nova mensagem" pra começar.
                </Typography>
              </Box>
            ) : (
              list.map(m => {
                const active = m.id === selectedId;
                return (
                  <Box
                    key={m.id}
                    onClick={() => {
                      if (dirty && !window.confirm('Descartar alterações?')) return;
                      setSelectedId(m.id);
                    }}
                    sx={{
                      p: '10px 14px', cursor: 'pointer',
                      bgcolor: active ? C.greenBg : 'transparent',
                      borderLeft: `3px solid ${active ? C.green : 'transparent'}`,
                      borderBottom: `1px solid ${C.bdr}`,
                      '&:hover': { bgcolor: active ? C.greenBg : C.surf },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.8} mb={0.3}>
                      {m.has_media && (m.media_type === 'IMAGE'
                        ? <FiImage size={11} color={C.blueD} />
                        : <FiFileText size={11} color={C.purple} />
                      )}
                      <Typography sx={{
                        fontFamily: SYNE, fontSize: '0.78rem', fontWeight: 700,
                        color: active ? C.greenD : C.text,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {m.name}
                      </Typography>
                    </Stack>
                    <Typography sx={{
                      fontFamily: SYNE, fontSize: '0.68rem', color: C.text3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {(m.body || '').slice(0, 60) || '(vazia)'}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* ── Editor + Preview ────────────────────────────────── */}
        {!selected ? (
          <Box sx={{
            bgcolor: C.card, border: `1px dashed ${C.bdr2}`, borderRadius: '10px',
            p: 6, textAlign: 'center',
          }}>
            <FiMessageSquare size={40} color={C.text3} style={{ opacity: 0.5 }} />
            <Typography sx={{ fontFamily: SYNE, fontSize: '0.9rem', fontWeight: 700, color: C.text2, mt: 2 }}>
              {list.length === 0 ? 'Crie sua primeira mensagem' : 'Selecione uma mensagem'}
            </Typography>
            <Typography sx={{ fontFamily: SYNE, fontSize: '0.76rem', color: C.text3, mt: 0.5 }}>
              {list.length === 0
                ? 'Depois de criar, ela aparece disponível pra cada chip na aba Gerenciamento.'
                : 'Escolha na lista à esquerda pra editar aqui.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            display: 'grid', gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: '1fr 320px' },
            alignItems: 'start',
          }}>
            {/* Editor */}
            <Box sx={{
              bgcolor: C.card, border: `1px solid ${C.bdr}`, borderRadius: '10px',
              boxShadow: C.sombra,
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{
                p: 2, borderBottom: `1px solid ${C.bdr}`,
              }}>
                <Typography sx={{ fontFamily: SYNE, fontWeight: 700, fontSize: '0.82rem', color: C.text }}>
                  Editar mensagem
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setConfirmDelete({ id: selected.id, name: selected.name })}
                  sx={{ color: C.redD, '&:hover': { bgcolor: C.redBg } }}
                >
                  <FiTrash2 size={14} />
                </IconButton>
              </Stack>
              <Box sx={{ p: 2 }}>
                <TextField
                  label="Nome interno"
                  value={draft.name}
                  onChange={e => { setDraft(d => ({ ...d, name: e.target.value })); setDirty(true); }}
                  fullWidth
                  size="small"
                  helperText="Só você vê — ex: 'Boas-vindas', 'Fora do horário', etc."
                  sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: SYNE, fontSize: '0.82rem' } }}
                />
                <TextField
                  label="Texto da mensagem"
                  value={draft.body}
                  onChange={e => {
                    if (e.target.value.length <= MAX_BODY) {
                      setDraft(d => ({ ...d, body: e.target.value }));
                      setDirty(true);
                    }
                  }}
                  fullWidth
                  multiline
                  minRows={6}
                  helperText={`${draft.body.length} / ${MAX_BODY} caracteres`}
                  sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: SYNE, fontSize: '0.82rem', lineHeight: 1.55 } }}
                />

                {/* Anexo */}
                <Box sx={{
                  p: 1.5, borderRadius: '8px', bgcolor: C.surf,
                  border: `1px dashed ${C.bdr2}`,
                }}>
                  <Typography sx={{
                    fontFamily: SYNE, fontSize: '0.7rem', fontWeight: 700,
                    color: C.text3, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1,
                  }}>
                    Anexo (opcional)
                  </Typography>
                  {selected.has_media ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {selected.media_type === 'IMAGE'
                        ? <FiImage size={16} color={C.blueD} />
                        : <FiFileText size={16} color={C.purple} />}
                      <Typography sx={{
                        fontFamily: SYNE, fontSize: '0.76rem', color: C.text,
                        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {selected.media_filename}
                      </Typography>
                      <Button
                        size="small"
                        onClick={removeMedia}
                        disabled={uploading}
                        startIcon={<FiX size={12} />}
                        sx={{
                          textTransform: 'none', fontFamily: SYNE, fontSize: '0.7rem',
                          color: C.redD, minWidth: 0,
                          '&:hover': { bgcolor: C.redBg },
                        }}
                      >
                        Remover
                      </Button>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Button
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={12} /> : <FiPaperclip size={12} />}
                        sx={{
                          textTransform: 'none', fontFamily: SYNE, fontWeight: 700, fontSize: '0.74rem',
                          bgcolor: C.card, color: C.text, border: `1px solid ${C.bdr}`, borderRadius: '8px',
                          '&:hover': { bgcolor: C.inset, border: `1px solid ${C.bdr2}` },
                        }}
                      >
                        {uploading ? 'Subindo…' : 'Anexar imagem ou PDF'}
                      </Button>
                      <Typography sx={{ fontFamily: SYNE, fontSize: '0.66rem', color: C.text3 }}>
                        Máx 5 MB
                      </Typography>
                    </Stack>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                    style={{ display: 'none' }}
                    onChange={e => uploadMedia(e.target.files?.[0])}
                  />
                </Box>
              </Box>
              <Stack direction="row" spacing={1} sx={{
                p: 2, borderTop: `1px solid ${C.bdr}`, bgcolor: C.surf, justifyContent: 'flex-end',
              }}>
                {dirty && (
                  <Chip
                    size="small"
                    label="Alterações não salvas"
                    sx={{
                      bgcolor: C.amberBg, color: C.amberD, border: `1px solid ${C.amberB}`,
                      fontFamily: SYNE, fontSize: '0.66rem', fontWeight: 700,
                      alignSelf: 'center', mr: 'auto',
                    }}
                  />
                )}
                <Button
                  onClick={saveDraft}
                  disabled={saving || !dirty}
                  startIcon={saving ? <CircularProgress size={12} /> : <FiSave size={12} />}
                  variant="contained"
                  sx={{
                    bgcolor: C.green, color: C.sobreCor, fontFamily: SYNE, fontWeight: 800,
                    textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
                    '&:hover': { bgcolor: C.greenHover, boxShadow: 'none' },
                    '&.Mui-disabled': { bgcolor: C.inset, color: C.text3 },
                  }}
                >
                  Salvar
                </Button>
              </Stack>
            </Box>

            {/* Preview WhatsApp */}
            <Box sx={{
              display: { xs: 'none', lg: 'block' },
            }}>
              <Typography sx={{
                fontFamily: SYNE, fontSize: '0.68rem', fontWeight: 700,
                color: C.text3, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.2,
              }}>
                Preview no WhatsApp
              </Typography>
              <Box sx={{
                bgcolor: C.waBg, borderRadius: '10px', p: 2,
                border: `1px solid ${C.bdr}`, minHeight: 200,
                backgroundImage: `radial-gradient(${C.waDot} 1px, transparent 1px)`,
                backgroundSize: '20px 20px',
              }}>
                <Box sx={{
                  bgcolor: C.waBubble, borderRadius: '8px', p: 1.2,
                  maxWidth: '90%', boxShadow: C.waSombra,
                }}>
                  {selected.has_media && selected.media_type === 'IMAGE' && (
                    <Box sx={{
                      width: '100%', height: 120, bgcolor: C.waMidia,
                      borderRadius: '4px', mb: 0.8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FiImage size={32} color={C.waMidiaInk} />
                    </Box>
                  )}
                  {selected.has_media && selected.media_type === 'DOCUMENT' && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{
                      p: 1, bgcolor: C.waDoc, borderRadius: '4px', mb: 0.8,
                    }}>
                      <FiFileText size={22} color={C.waMidiaInk} />
                      <Typography sx={{ fontFamily: OUTFIT, fontSize: '0.72rem', color: C.waDocInk }}>
                        {selected.media_filename}
                      </Typography>
                    </Stack>
                  )}
                  <Typography sx={{
                    fontFamily: OUTFIT, fontSize: '0.82rem', color: C.waInk, lineHeight: 1.45,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {draft.body || <Box component="span" sx={{ color: C.waVazio }}>(sem texto)</Box>}
                  </Typography>
                  <Typography sx={{
                    fontFamily: OUTFIT, fontSize: '0.6rem', color: C.waInk2,
                    textAlign: 'right', mt: 0.3,
                  }}>
                    12:34 <FiCheck size={11} color="#4fc3f7" style={{ verticalAlign: 'middle', marginLeft: 2 }} />
                    <FiCheck size={11} color="#4fc3f7" style={{ verticalAlign: 'middle', marginLeft: -6 }} />
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Confirmar delete */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800 }}>Apagar mensagem?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: SYNE, fontSize: '0.82rem', color: C.text2 }}>
            "<b>{confirmDelete?.name}</b>" será apagada. Chips que usavam ela serão
            desassociados automaticamente.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none', fontFamily: SYNE, color: C.text2 }}>
            Cancelar
          </Button>
          <Button
            onClick={doDelete}
            disabled={saving}
            variant="contained"
            startIcon={saving ? <CircularProgress size={12} /> : <FiTrash2 size={12} />}
            sx={{
              bgcolor: C.red, color: C.sobreCor, fontFamily: SYNE, fontWeight: 800,
              textTransform: 'none', borderRadius: 2,
              '&:hover': { bgcolor: C.redHover },
            }}
          >
            Apagar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
